import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Key } from "lucide-react";

export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1); // 1: email, 2: code + password
  const [email, setEmail] = useState("");
  const [formData, setFormData] = useState({
    reset_code: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post("/auth/forgot-password", { email });
      toast.success("Reset code sent to your email!");
      setStep(2);
    } catch (error) {
      toast.error("Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/auth/reset-password-with-code", {
        email,
        reset_code: formData.reset_code,
        new_password: formData.new_password
      });
      toast.success("Password reset successfully! You can now login.");
      setStep(1);
      setEmail("");
      setFormData({ reset_code: "", new_password: "", confirm_password: "" });
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail("");
    setFormData({ reset_code: "", new_password: "", confirm_password: "" });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {step === 1 
              ? "Enter your email to receive a reset code" 
              : "Enter the code sent to your email and set a new password"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your.email@example.com"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                data-testid="cancel-forgot-password-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="send-reset-code-button"
              >
                {loading ? "Sending..." : "Send Reset Code"}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="reset_code">Reset Code</Label>
              <div className="relative mt-1">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="reset_code"
                  data-testid="reset-code-input"
                  type="text"
                  value={formData.reset_code}
                  onChange={(e) => setFormData({ ...formData, reset_code: e.target.value })}
                  required
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="pl-10 text-center text-2xl tracking-widest font-bold"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Check your email for the reset code</p>
            </div>
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                data-testid="forgot-new-password-input"
                type="password"
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
                placeholder="Enter new password"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                data-testid="forgot-confirm-password-input"
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
                placeholder="Confirm new password"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                data-testid="back-to-email-button"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-reset-password-button"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
